import { useState } from 'react';
import { Link } from 'react-router-dom';
import { OptionChips } from '../../components/OptionChips';
import { isPlanError } from '../../components/plan/planErrors';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { useQuota } from '../../components/quota/useQuota';
import type { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatDate } from '../../lib/format';
import {
  PLATFORM_OPTIONS,
  TEXT_ONLY_PLATFORMS,
  TEXT_ONLY_UNAVAILABLE_REASON,
} from '../../lib/platforms';
import { domainOf, safeExternalUrl } from '../../lib/url';
import type {
  BusinessRead,
  NewsAutopostAutomation,
  NewsAutopostSettingsRead,
  NewsAutopostSettingsUpdate,
  PlatformName,
  PostType,
} from '../../types/api';
import { IntentPicker } from '../generation/IntentPicker';
import { knownIntents } from '../generation/intents';
import { useIntents } from '../generation/useIntents';
import { knowledgeBaseSectionPath } from '../knowledge-base/sections';
import { useConnections } from '../knowledge-base/useConnections';
import { saveNewsAutopost, useNewsAutopost } from './newsAutopostApi';

const MODE_OPTIONS: readonly { value: NewsAutopostAutomation; label: string }[] = [
  { value: 'review', label: 'Create for my review' },
  { value: 'auto', label: 'Post automatically' },
];

const MODE_HINTS: Readonly<Record<NewsAutopostAutomation, string>> = {
  review:
    'A draft off the freshest headline is waiting on the calendar each day, parked for you to pick the caption and approve. Nothing is posted.',
  auto: 'The caption is chosen by policy and posted to LinkedIn each day with nobody watching. A headline that fails the fact check waits for you instead.',
};

const POST_TYPE_OPTIONS: readonly { value: PostType; label: string }[] = [
  { value: 'single_image', label: 'Single image' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'text_only', label: 'Text only' },
];

/** The form's own copy of the row. Only what the owner edits. */
interface AutopostValues {
  enabled: boolean;
  automation: NewsAutopostAutomation;
  platform: PlatformName;
  post_type: PostType;
  /** What the daily post is for, in pick order. */
  intents: string[];
  local_time: string;
  timezone: string;
}

function fromSettings(settings: NewsAutopostSettingsRead): AutopostValues {
  return {
    enabled: settings.enabled,
    automation: settings.automation,
    platform: settings.platform,
    post_type: settings.post_type,
    intents: settings.intents,
    local_time: settings.local_time,
    timezone: settings.timezone,
  };
}

/** The zone the browser is in, as the IANA name the server wants. */
function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** What happened last time, in a sentence. */
function lastRunLine(settings: NewsAutopostSettingsRead): string | null {
  if (!settings.last_run_on) return null;
  const day = formatDate(settings.last_run_on);
  switch (settings.last_error) {
    case null:
      return `Last planned ${day}.`;
    case 'no_fresh_item':
      return `Nothing fresh in the feed on ${day}, so that day was skipped.`;
    default:
      return `The post on ${day} failed. We try again tomorrow.`;
  }
}

interface NewsAutopostCardProps {
  business: BusinessRead;
}

/**
 * Daily news autopost: every day, at a local time, plan a post off the
 * freshest headline in the newsfeed and run it.
 *
 * Owner-editable; a member sees it read-only, which is also what the database
 * says. The timezone is captured from the browser when the owner saves with
 * none set, and shown rather than picked: the one time zone that matters is
 * the one the person setting the clock is standing in. *Post automatically*
 * is LinkedIn-only and needs a live connection, so the card says so before
 * the server refuses the same thing.
 */
export function NewsAutopostCard({ business }: NewsAutopostCardProps) {
  const isOwner = business.role === 'owner';
  const autopost = useNewsAutopost(business.id);
  const connections = useConnections(business.id);
  const { quota } = useQuota();

  if (autopost.isLoading || autopost.settings === null) {
    return (
      <section className="wizard settings-card">
        <div className="settings-card__head">
          <h2 className="wizard__title">Autopost</h2>
          <p className="settings-card__lede" role="status">
            {autopost.error ?? 'Loading'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <NewsAutopostForm
      key={business.id}
      business={business}
      isOwner={isOwner}
      settings={autopost.settings}
      onSaved={autopost.replace}
      linkedinReady={
        connections.linkedin !== null &&
        connections.linkedin.available &&
        connections.linkedin.status === 'connected'
      }
      linkedinStatus={connections.linkedin?.status ?? null}
      quotaLine={quota ? `${quota.used} of ${quota.limit} posts used this month.` : null}
    />
  );
}

interface NewsAutopostFormProps {
  business: BusinessRead;
  isOwner: boolean;
  settings: NewsAutopostSettingsRead;
  onSaved: (settings: NewsAutopostSettingsRead) => void;
  linkedinReady: boolean;
  linkedinStatus: string | null;
  quotaLine: string | null;
}

function NewsAutopostForm({
  business,
  isOwner,
  settings,
  onSaved,
  linkedinReady,
  linkedinStatus,
  quotaLine,
}: NewsAutopostFormProps) {
  const [values, setValues] = useState<AutopostValues>(() => fromSettings(settings));
  const [savedFrom, setSavedFrom] = useState<AutopostValues>(() => fromSettings(settings));
  const [error, setError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { can } = usePlan();
  // Switching on needs the daily news post; switching off never does, so a
  // brand left on after a downgrade can still be turned off here.
  const canSwitchOn = can('daily_news');
  // Posting with nobody watching is Autopilot, here as on the calendar.
  const canAuto = can('autopilot');

  // A purpose the registry no longer carries is dropped on the way out, so a
  // retired key cannot sit on a row the worker reads every morning.
  const { intents: intentRegistry } = useIntents();
  const selectedIntents = knownIntents(values.intents, intentRegistry);

  const isDirty = JSON.stringify(values) !== JSON.stringify(savedFrom);
  const lastItemHref = safeExternalUrl(settings.last_item_url);
  const lastRun = lastRunLine(settings);
  const needsLinkedIn = values.enabled && values.automation === 'auto' && !linkedinReady;

  function patch(changes: Partial<AutopostValues>) {
    setError(null);
    setPlanError(null);
    setNotice(null);
    setValues((current) => ({ ...current, ...changes }));
  }

  function chooseMode(value: string) {
    const automation = value === 'auto' ? 'auto' : 'review';
    // Unattended posting is LinkedIn-only: choosing it also chooses the platform.
    patch(automation === 'auto' ? { automation, platform: 'linkedin' } : { automation });
  }

  async function handleSubmit() {
    if (needsLinkedIn) {
      setError('Connect a LinkedIn account before switching on automatic posting.');
      return;
    }
    const body: NewsAutopostSettingsUpdate = {
      ...values,
      intents: selectedIntents,
      // A row never saved carries the server's UTC default; the owner's own
      // clock is the one that matters, so it is filled in on the first save.
      timezone:
        settings.updated_at === null && values.timezone === 'UTC'
          ? (browserTimezone() ?? values.timezone)
          : values.timezone,
    };
    setError(null);
    setPlanError(null);
    setIsSaving(true);
    try {
      const result = await saveNewsAutopost(business.id, body);
      onSaved(result);
      setSavedFrom(fromSettings(result));
      setValues(fromSettings(result));
      setNotice(result.enabled ? 'Saved. The first post is planned at the next slot.' : 'Saved.');
    } catch (caught) {
      if (isPlanError(caught)) setPlanError(caught);
      else setError(getErrorMessage(caught, 'Could not save. Try again.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="wizard settings-card">
      <form
        className="brief-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="settings-card__head">
          <h2 className="wizard__title">Autopost</h2>
          <p className="settings-card__lede">
            Every day at a time you set, we make a post from the freshest headline in your newsfeed.
            Each day uses one post.
            {!isOwner ? ' Only the owner can change this.' : ''}
          </p>
        </div>

        <fieldset className="brief-form__fields" disabled={!isOwner}>
          <label className="field field--inline autopost-switch">
            <input
              type="checkbox"
              checked={values.enabled}
              disabled={!values.enabled && !canSwitchOn}
              onChange={(event) => patch({ enabled: event.target.checked })}
            />
            <span className="field__label">Autopost one news story a day</span>
          </label>
          {canSwitchOn ? null : <Upsell feature="daily_news" />}

          <OptionChips
            legend="Each day"
            hint={MODE_HINTS[values.automation]}
            options={MODE_OPTIONS}
            selected={[values.automation]}
            mode="single"
            name="news-autopost-mode"
            onToggle={chooseMode}
            unavailable={canAuto ? [] : ['auto']}
          />
          {canAuto || !canSwitchOn ? null : <Upsell feature="autopilot" />}

          {needsLinkedIn ? (
            <p className="form__notice">
              {linkedinStatus === 'expired'
                ? 'Your LinkedIn connection has expired. '
                : 'No LinkedIn account is connected yet. '}
              <Link to={knowledgeBaseSectionPath('integrations')}>
                {linkedinStatus === 'expired' ? 'Reconnect it' : 'Connect one'} in Connected
                accounts.
              </Link>
            </p>
          ) : null}

          <OptionChips
            legend="Platform"
            hint={
              values.automation === 'auto'
                ? 'Automatic posting goes to LinkedIn.'
                : 'The caption is written to this platform’s limits.'
            }
            options={
              values.automation === 'auto'
                ? PLATFORM_OPTIONS.filter((option) => option.value === 'linkedin')
                : PLATFORM_OPTIONS
            }
            selected={[values.platform]}
            mode="single"
            name="news-autopost-platform"
            onToggle={(value) => {
              const platform = PLATFORM_OPTIONS.find((option) => option.value === value);
              if (!platform) return;
              // Instagram and TikTok take no text-only post: fall back to a single image.
              patch(
                values.post_type === 'text_only' && !TEXT_ONLY_PLATFORMS.includes(platform.value)
                  ? { platform: platform.value, post_type: 'single_image' }
                  : { platform: platform.value },
              );
            }}
          />

          <OptionChips
            legend="Post type"
            hint={
              TEXT_ONLY_PLATFORMS.includes(values.platform)
                ? 'A single image is one generated square; a carousel is three to five slides; text only is the caption alone.'
                : `A single image is one generated square; a carousel is three to five slides. ${TEXT_ONLY_UNAVAILABLE_REASON}`
            }
            options={POST_TYPE_OPTIONS}
            unavailable={TEXT_ONLY_PLATFORMS.includes(values.platform) ? [] : ['text_only']}
            selected={[values.post_type]}
            mode="single"
            name="news-autopost-post-type"
            onToggle={(value) => {
              const postType = POST_TYPE_OPTIONS.find((option) => option.value === value);
              if (postType) patch({ post_type: postType.value });
            }}
          />

          {/* A post about someone else's article is informational by default,
              which is what the server seeds the row with — but a daily post
              that always explains is a choice, so it is the owner's to make. */}
          <IntentPicker
            legend="What the daily post is for"
            hint="Up to two — the first is what the post leads with. Pick none and we decide each day."
            value={selectedIntents}
            onChange={(next) => patch({ intents: next })}
            disabled={!isOwner}
          />

          <div className="brief-form__row">
            <label className="field">
              <span className="field__label">Time</span>
              <input
                className="field__input"
                type="time"
                value={values.local_time}
                onChange={(event) => patch({ local_time: event.target.value })}
              />
              <span className="field__hint">
                {values.automation === 'review'
                  ? 'The draft is ready around this time.'
                  : 'The post lands within a quarter hour of this.'}
              </span>
            </label>
            <div className="field">
              <span className="field__label">Time zone</span>
              <span className="field__input autopost-zone">
                {settings.updated_at === null
                  ? (browserTimezone() ?? values.timezone)
                  : values.timezone}
              </span>
              <span className="field__hint">
                {settings.updated_at === null
                  ? 'Taken from this browser when you save.'
                  : 'Set when this was first saved.'}
              </span>
            </div>
          </div>
        </fieldset>

        {lastRun || quotaLine ? (
          <p className="field__hint autopost-status">
            {lastRun}
            {lastRun && lastItemHref ? (
              <>
                {' '}
                <a href={lastItemHref} target="_blank" rel="noopener noreferrer">
                  {domainOf(lastItemHref) ?? lastItemHref}
                </a>
              </>
            ) : null}
            {lastRun && quotaLine ? ' · ' : ''}
            {quotaLine}
          </p>
        ) : null}

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
        <UpsellFromError error={planError} />
        {notice ? (
          <p className="form__notice" role="status">
            {notice}
          </p>
        ) : null}

        {isOwner ? (
          <div className="wizard__actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={isSaving || !isDirty}
            >
              {isSaving ? 'Saving' : 'Save changes'}
            </button>
          </div>
        ) : null}
      </form>
    </section>
  );
}
